import type { SubscriptionResponse } from '@kimuno/api-sdk/lib/types/responses/Billing';

export enum SuspendedPageType {
	SUSPENDED = 0,
	PAUSED = 1,
}

export const isSuspended = (subscription: SubscriptionResponse | undefined) => {
	return subscription?.status === 'Suspended' && !subscription.suspension?.isPause;
};

export const isPaused = (subscription: SubscriptionResponse | undefined) => {
	return subscription?.status === 'Suspended' && subscription.suspension?.isPause;
};

export const getSuspensionType = (subscription: SubscriptionResponse | undefined) => {
	return isPaused(subscription) ? SuspendedPageType.PAUSED : SuspendedPageType.SUSPENDED;
};

/**
 * Whether the subscription is a trial of ANY kind: currently within the trial period
 * (`trial.isInTrial`), a licence that includes a trial (`licence.hasTrial`, which stays true
 * after converting to Premium), or a B2B school trial (`licence.isSchoolTrial`). Single source
 * of truth for "is this a trial?" checks.
 */
export const isTrialSubscription = (subscription: SubscriptionResponse | undefined): boolean =>
	!!subscription?.trial?.isInTrial ||
	!!subscription?.licence?.hasTrial ||
	!!subscription?.licence?.isSchoolTrial;

/**
 * A subscription is renewed (on its 2nd+ term) once the current term's `startDate` has moved
 * past the original contract-conclusion date `originalCreatedDate`. There is no explicit backend
 * flag for this — the date inequality is the canonical signal across the app (analytics in
 * ampli.ts, withdrawal eligibility in withdrawalEligibility.ts).
 *
 * NOTE: trials cannot be renewed. Callers that need to exclude trial-origin subscriptions must
 * do so separately, since the appropriate trial signal differs by context (e.g. `licence.hasTrial`
 * vs `trial.isInTrial`).
 */
export const isRenewedSubscription = (subscription: SubscriptionResponse | undefined): boolean => {
	// Require both dates so a missing field is never mistaken for a renewal (default: not renewed).
	return (
		!!subscription &&
		!!subscription.startDate &&
		!!subscription.originalCreatedDate &&
		subscription.startDate !== subscription.originalCreatedDate
	);
};
